import { activeNavItemAtom } from '@/global-store';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SidebarNavButton from '@layout/Sidebar/MenuList/SidebarNavButton';
import NavItemLabel from '@layout/Sidebar/MenuList/NavItemLabel';
import { useBadgeVisibility } from '@layout/Sidebar/MenuList/useBadgeVisibility';
import { useSidebar } from '@layout/Sidebar/SidebarContext';
import type { MenuItem } from '@layout/Sidebar/MenuList/types';

interface NavItemProps {
  item: MenuItem;
  level?: number;
  onEscapeCollapse?: () => void;
}

function NavItem({ item, level = 0, onEscapeCollapse }: NavItemProps) {
  const { t } = useTranslation();
  const [activeNavItem, setActiveNavItem] = useAtom(activeNavItemAtom);
  const { isMobile, setOpenMobile } = useSidebar();
  const showBadge = useBadgeVisibility(item.handle?.badge);
  const { pathname, search } = useLocation();

  const selected = activeNavItem === item.path;
  const isExternal = Boolean(item.handle?.external);
  const { badgeText, title } = getUIStrings(item, t);

  useEffect(() => {
    const {
      activeOnPaths = [],
      inactiveOnPaths = [],
      activeOnQueryIncludes = [],
      inactiveOnQueryIncludes = [],
    } = item.handle ?? {};

    const matchesPath =
      item.path === '/' ? pathname === '/' : pathname.includes(item.path);

    const matchesActive =
      matchesPath ||
      activeOnPaths?.some((path) => pathname.startsWith(path)) ||
      activeOnQueryIncludes?.some((part) => search.includes(part));

    const matchesInactive =
      inactiveOnPaths?.some((path) => pathname.startsWith(path)) ||
      inactiveOnQueryIncludes?.some((part) => search.includes(part));

    if (matchesActive && !matchesInactive) {
      setActiveNavItem(item.path);
    }
  }, [item.handle, item.path, pathname, search, setActiveNavItem]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || !onEscapeCollapse) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onEscapeCollapse?.();
  };

  return (
    <SidebarNavButton
      component={isExternal ? 'a' : Link}
      target={item.target ? '_blank' : '_self'}
      to={isExternal ? undefined : item.path}
      href={isExternal ? item.path : undefined}
      rel={isExternal ? 'noreferrer' : undefined}
      level={level}
      selected={selected}
      aria-label={title + (showBadge && badgeText ? ` - ${badgeText}` : '')}
      role='link'
      aria-current={selected ? 'page' : undefined}
      disabled={item.disabled}
      onClick={() => {
        setActiveNavItem(item.path);
        if (isMobile) {
          setOpenMobile(false);
        }
      }}
      onKeyDown={handleKeyDown}
      tooltip={
        item.handle?.i18nTooltipKey ? t(item.handle.i18nTooltipKey) : undefined
      }
    >
      <NavItemLabel item={item} title={title} />
    </SidebarNavButton>
  );
}

function getUIStrings(item: MenuItem, t: TFunction) {
  const badgeText =
    typeof item.handle?.badge?.text === 'string' ? item.handle.badge.text : '';

  const title = item.handle?.i18nNameKey
    ? t(item.handle.i18nNameKey)
    : (item.handle?.name ?? '');

  return { badgeText, title };
}

export default NavItem;
